import { useCallback, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ShieldCheck, X } from 'lucide-react';
import { NAV_EXACT_PATHS, NAV_SECTIONS, isNavGroup, type NavGroup, type NavItem } from '@/app/navigation';
import { prefetchRoute } from '@/app/prefetch';
import { BrandMark } from '@/components/BrandMark';
import { useSessionUser } from '@/lib/auth';
import { hasAtLeastRole, Role } from '@/types/domain';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// The 260px navigation rail.
//
// DARK, against the light workspace. That is a structural decision, not a
// stylistic one: with a white rail on a white canvas the console had no visual
// hierarchy above the level of individual words, and every region looked like
// every other one. A dark rail separates chrome from content pre-attentively -
// you find the nav without reading it.
//
// The rail is also the one place lavender works as TEXT. #C8A4F6 is 1.9:1 on
// white and can only ever be a surface there; on #111827 it is 7.4:1, so the
// brand colour carries the active row instead of a neutral standing in for it.
//
// ONE FLAT LIST, NO GROUP HEADINGS. The rail used to print a small-caps label
// over each group - OVERVIEW, MODERATION, PEOPLE - which is six rows of text
// that are never a destination sitting between the rows that are. The groups
// survive as SPACING and a hairline, which says "these belong together" without
// spending a row saying it.
//
// ONE EXCEPTION: a NESTED row. Messaging is a single concern with three
// surfaces, and three siblings in the rail claimed the weight of three
// unrelated destinations. It expands instead. The parent does NOT navigate -
// a row that both expands and goes somewhere is a coin toss every time you
// click it - and it opens automatically when one of its children is the current
// route, so a deep link never lands on a screen whose nav row is collapsed.
//
// Three details do the rest:
//   • The active row is a flat violet-black fill with a 3px lavender bar on its
//     left edge. The bar is what makes the state legible at a glance down a
//     column of rows.
//   • Icons change colour with state but never size. An icon that grows on
//     hover reflows the label beside it, and a nav that twitches under the
//     cursor feels cheap.
//   • Count badges are the ONLY red in the rail, so red means "something is
//     waiting" and nothing else.
//
// BELOW `lg` the same component renders inside a Sheet instead of the fixed
// rail. Not a second, cut-down mobile nav - the identical markup, so a rail item
// added here appears on a phone with no further work.
// -----------------------------------------------------------------------------

type Counts = { pendingApplications: number; reviewQueue: number; openTickets: number };

export function Sidebar({
  counts,
  onNavigate,
}: {
  counts: Counts;
  /** Present only in the mobile drawer. Fires after a nav item is activated. */
  onNavigate?: () => void;
}): JSX.Element {
  const user = useSessionUser();
  const queryClient = useQueryClient();

  // Hover and keyboard focus both signal intent and both precede activation by
  // enough time to download a route chunk. `prefetchRoute` is idempotent.
  const warm = useCallback(
    (path: string) => () => prefetchRoute(path, queryClient),
    [queryClient],
  );

  const isSuperAdmin = hasAtLeastRole(user.role, Role.SUPER_ADMIN);

  const visible = (item: { minimumRole?: Role }): boolean =>
    !item.minimumRole || hasAtLeastRole(user.role, item.minimumRole);

  // Resolved once, so the separator logic can ask "is this the last group that
  // renders anything?" rather than "is this the last group declared?" - an admin
  // sees no System section at all and must not get a trailing hairline under the
  // final item they can actually reach.
  const groups = NAV_SECTIONS.map((section) =>
    section.items
      .filter(visible)
      .map((entry) =>
        isNavGroup(entry) ? { ...entry, children: entry.children.filter(visible) } : entry,
      )
      .filter((entry) => !isNavGroup(entry) || entry.children.length > 0),
  ).filter((items) => items.length > 0);

  return (
    <nav aria-label="Primary" className="flex h-full w-full min-w-0 flex-col bg-sidebar">
      <div className="flex h-topbar shrink-0 items-center gap-2 border-b border-sidebar-border px-3">
        <BrandMark size="sm" tagline="Admin" inverted />

        {/* The drawer's own close control, in rail colours. The Sheet primitive's
            default one is styled for a light surface and would be a grey glyph
            on #111827 - see `hideClose` in `ui/sheet.tsx`. */}
        {onNavigate ? (
          <button
            type="button"
            onClick={onNavigate}
            aria-label="Close navigation"
            className="ml-auto shrink-0 rounded p-1.5 text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-active-fg"
          >
            <X className="size-4" aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-2">
        {groups.map((items, index) => (
          <ul
            key={index}
            className={cn(
              'flex min-w-0 flex-col gap-0.5',
              // A hairline plus breathing room between groups, drawn only
              // BETWEEN them - a rule under the last group would read as the
              // start of a section that is not there.
              index < groups.length - 1 && 'mb-2 border-b border-sidebar-border pb-2',
            )}
          >
            {items.map((entry) =>
              isNavGroup(entry) ? (
                <NavDisclosure
                  key={entry.label}
                  group={entry}
                  counts={counts}
                  warm={warm}
                  onNavigate={onNavigate}
                />
              ) : (
                <li key={entry.to} className="min-w-0">
                  <NavRow item={entry} counts={counts} warm={warm} onNavigate={onNavigate} />
                </li>
              ),
            )}
          </ul>
        ))}
      </div>

      {/* Access tier, stated plainly. An operator who cannot find the Staff
          screen should be able to see why without asking. */}
      <div className="shrink-0 border-t border-sidebar-border p-2">
        <div className="flex min-w-0 items-center gap-2 rounded-md bg-sidebar-hover px-2.5 py-2">
          <ShieldCheck
            className={cn(
              'size-4 shrink-0',
              isSuperAdmin ? 'text-sidebar-active-fg' : 'text-sidebar-muted',
            )}
            aria-hidden
          />
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold leading-tight text-sidebar-foreground">
              {isSuperAdmin ? 'Super admin access' : 'Admin access'}
            </p>
            <p className="truncate text-[11px] leading-tight text-sidebar-muted">
              {isSuperAdmin ? 'Full control. Audited.' : 'Moderation and support.'}
            </p>
          </div>
        </div>
      </div>
    </nav>
  );
}

/** Shared row chrome, so a nested link is visibly the same object as a top one. */
const ROW = cn(
  'group relative flex min-w-0 items-center gap-2.5 rounded-md py-1.5 pl-2.5 pr-2 text-sm font-medium',
  'transition-colors duration-150',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-active-fg focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
);

function NavRow({
  item,
  counts,
  warm,
  onNavigate,
  nested = false,
}: {
  item: NavItem;
  counts: Counts;
  warm: (path: string) => () => void;
  onNavigate?: () => void;
  nested?: boolean;
}): JSX.Element {
  const count = item.badgeKey ? counts[item.badgeKey] : 0;

  return (
    <NavLink
      to={item.to}
      // Exact match for a path another nav row sits under (`/`, `/wallet`),
      // otherwise both rows light up. Every other row keeps prefix matching,
      // so a screen's own sub-routes still highlight it.
      end={NAV_EXACT_PATHS.has(item.to)}
      onPointerEnter={warm(item.to)}
      onFocus={warm(item.to)}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          ROW,
          isActive
            ? 'bg-sidebar-active-bg text-sidebar-active-fg'
            : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Absolutely positioned so it cannot shift the label by a pixel when
              it appears. A nested row's bar sits on the guide line. */}
          <span
            aria-hidden
            className={cn(
              'absolute top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-sidebar-active-fg transition-opacity duration-150',
              nested ? '-left-[9px]' : 'left-0',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />

          <item.icon
            className={cn(
              'size-4 transition-colors',
              isActive
                ? 'text-sidebar-active-fg'
                : 'text-sidebar-muted group-hover:text-sidebar-foreground',
            )}
            aria-hidden
          />

          {/* `title` gives the full label on hover for the one item long enough
              to truncate at 260px. Truncation with no way to read the whole
              thing is a bug. */}
          <span className="min-w-0 flex-1 truncate" title={item.label}>
            {item.label}
          </span>

          {count > 0 ? (
            <span className="shrink-0 rounded bg-destructive px-1.5 py-0.5 text-[11px] font-semibold leading-4 text-white tabular">
              {count > 99 ? '99+' : count}
              <span className="sr-only"> awaiting action</span>
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

/**
 * A nav row that expands.
 *
 * Opens automatically when one of its children is the current route, and stays
 * open once opened by hand. The `useState` initialiser reads the location
 * rather than an effect setting it afterwards: an effect would render the
 * collapsed state for one frame on every deep link, which is a visible flicker
 * on the screen you just navigated to.
 *
 * The parent is a `<button>`, not a link. See the note at the top of the file.
 */
function NavDisclosure({
  group,
  counts,
  warm,
  onNavigate,
}: {
  group: NavGroup;
  counts: Counts;
  warm: (path: string) => () => void;
  onNavigate?: () => void;
}): JSX.Element {
  const { pathname } = useLocation();
  const holdsActive = group.children.some(
    (child) => pathname === child.to || pathname.startsWith(`${child.to}/`),
  );
  const [open, setOpen] = useState(holdsActive);

  // A deep link into a child while the group is collapsed must open it. Derived
  // rather than stored, so it cannot disagree with the route.
  const expanded = open || holdsActive;

  return (
    <li className="min-w-0">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          ROW,
          'w-full text-left',
          // The parent shows the collapsed group's state: when a child is active
          // but the list is shut, the row still has to say "you are in here".
          holdsActive && !expanded
            ? 'bg-sidebar-active-bg text-sidebar-active-fg'
            : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground',
        )}
      >
        <group.icon
          className={cn(
            'size-4 transition-colors',
            holdsActive
              ? 'text-sidebar-active-fg'
              : 'text-sidebar-muted group-hover:text-sidebar-foreground',
          )}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate">{group.label}</span>
        <ChevronRight
          className={cn(
            'size-3.5 transition-transform duration-150',
            expanded && 'rotate-90',
          )}
          aria-hidden
        />
      </button>

      {expanded ? (
        // The guide line is what makes the indent read as containment rather
        // than as an arbitrary offset - without it, three shifted rows just look
        // misaligned.
        <ul className="ml-[18px] mt-0.5 flex min-w-0 flex-col gap-0.5 border-l border-sidebar-border pl-2">
          {group.children.map((child) => (
            <li key={child.to} className="min-w-0">
              <NavRow
                item={child}
                counts={counts}
                warm={warm}
                onNavigate={onNavigate}
                nested
              />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}
